import React, {useCallback, useEffect, useRef, useState} from "react";
import {Button, Modal, Spinner} from "react-bootstrap";
import axios from "axios";

// Beschriftung und Reihenfolge der Quellen. Die Schlüssel entsprechen denen,
// die der Endpunkt liefert.
const QUELLEN = [
    {
        id: "weclapp",
        name: "Weclapp",
        beschreibung: "Lagerbestände, Reservierungen, offen nicht reservierte Mengen und Preise",
        teile: [
            ["stock_synced_at", "Lagerbestand"],
            ["reserved_synced_at", "Reserviert / offen, nicht reserviert"],
            ["prices_synced_at", "Preise"],
        ],
    },
    {
        id: "b2c",
        name: "B2C-Shop",
        beschreibung: "Freischaltung, Lagerstatus und Farbwerte aus shop.molotow.com",
        teile: [],
    },
    {
        id: "b2b",
        name: "B2B-Shop",
        beschreibung: "Freischaltung und Lagerstatus aus b2b.molotow.com",
        teile: [],
    },
];

// Zeitangaben kommen als ISO 8601 mit Zeitzonen-Offset. Der Browser rechnet
// selbst in die Ortszeit um — deshalb hier bewusst keine eigene Umrechnung.
function zeitpunkt(iso) {
    if (! iso) return "noch nie abgerufen";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "unbekannt";
    return d.toLocaleString("de-DE", {
        day: "2-digit", month: "2-digit", year: "numeric",
        hour: "2-digit", minute: "2-digit",
    }) + " Uhr";
}

// „vor 5 Minuten" ist beim Beurteilen der Aktualität hilfreicher als das
// nackte Datum, deshalb steht beides nebeneinander.
function abstand(iso) {
    if (! iso) return null;
    const sekunden = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (! Number.isFinite(sekunden) || sekunden < 0) return null;
    if (sekunden < 90) return "gerade eben";
    const minuten = Math.floor(sekunden / 60);
    if (minuten < 60) return `vor ${minuten} Minuten`;
    const stunden = Math.floor(minuten / 60);
    if (stunden < 24) return `vor ${stunden} ${stunden === 1 ? "Stunde" : "Stunden"}`;
    const tage = Math.floor(stunden / 24);
    return `vor ${tage} ${tage === 1 ? "Tag" : "Tagen"}`;
}

function restsekunden(lockedUntil) {
    if (! lockedUntil) return 0;
    return Math.max(0, Math.ceil((new Date(lockedUntil).getTime() - Date.now()) / 1000));
}

// Wie lange der laufende Abruf schon dauert — gerechnet aus dem
// Startzeitpunkt vom Server, damit alle Nutzer denselben Wert sehen.
function laufzeit(triggeredAt) {
    if (! triggeredAt) return 0;
    return Math.max(0, Math.floor((Date.now() - new Date(triggeredAt).getTime()) / 1000));
}

function dauerText(sekunden) {
    if (sekunden < 60) return `${sekunden} Sekunden`;
    const m = Math.floor(sekunden / 60);
    const s = sekunden % 60;
    return s === 0 ? `${m} ${m === 1 ? "Minute" : "Minuten"}` : `${m}:${String(s).padStart(2, "0")} Minuten`;
}

function uhrzeitAusSekunden(gesamt) {
    const m = Math.floor(gesamt / 60);
    const s = gesamt % 60;
    return `${m}:${String(s).padStart(2, "0")}`;
}

export default function DataFreshnessButton() {
    const [offen, setOffen] = useState(false);
    const [daten, setDaten] = useState(null);
    const [laedt, setLaedt] = useState(false);
    const [fehler, setFehler] = useState(null);
    const [laeuft, setLaeuft] = useState(null);
    const [frageAlle, setFrageAlle] = useState(false);
    // Solange irgendeine Quelle läuft, ruhen alle Knöpfe.
    const [beschaeftigt, setBeschaeftigt] = useState(false);
    // Eigener Zähler, damit die Restzeit jede Sekunde neu gezeichnet wird,
    // ohne dafür den Server zu fragen.
    const [, tick] = useState(0);
    const abbruch = useRef(null);

    const laden = useCallback(async () => {
        setLaedt(true);
        setFehler(null);
        try {
            abbruch.current?.abort();
            abbruch.current = new AbortController();
            const antwort = await axios.get("/stock/overview/data-freshness", {signal: abbruch.current.signal});
            setDaten(antwort.data?.sources ?? null);
            setBeschaeftigt(Boolean(antwort.data?.busy));
        } catch (e) {
            if (! axios.isCancel(e)) {
                setFehler("Der Datenstand konnte nicht geladen werden.");
            }
        } finally {
            setLaedt(false);
        }
    }, []);

    useEffect(() => {
        if (offen) laden();
        return () => abbruch.current?.abort();
    }, [offen, laden]);

    // Sekundentakt nur, solange das Fenster offen ist.
    useEffect(() => {
        if (! offen) return undefined;
        const timer = window.setInterval(() => tick((n) => n + 1), 1000);
        return () => window.clearInterval(timer);
    }, [offen]);

    // Solange ein Abruf läuft, in Ruhe nachsehen, ob er fertig ist.
    useEffect(() => {
        if (! offen || ! daten) return undefined;
        if (! beschaeftigt) return undefined;
        // Häufiger nachsehen, solange etwas läuft — der Wechsel von „wartend"
        // auf „läuft" soll zeitnah sichtbar werden.
        const timer = window.setInterval(laden, 5000);
        return () => window.clearInterval(timer);
    }, [offen, beschaeftigt, laden]);

    const aktualisieren = async (quelle) => {
        setLaeuft(quelle);
        setFehler(null);
        try {
            await axios.post(`/stock/overview/data-freshness/${quelle}`);
            await laden();
        } catch (e) {
            if (e.response?.status === 429) {
                await laden();
            } else {
                setFehler("Der Abruf konnte nicht gestartet werden.");
            }
        } finally {
            setLaeuft(null);
        }
    };

    const alleAktualisieren = async () => {
        setFrageAlle(false);
        setLaeuft("alle");
        setFehler(null);
        try {
            await axios.post("/stock/overview/data-freshness-all");
            await laden();
        } catch (e) {
            setFehler("Der Sammelabruf konnte nicht gestartet werden.");
        } finally {
            setLaeuft(null);
        }
    };

    // Frei ist, was weder gesperrt ist noch gerade läuft.
    const freieQuellen = daten
        ? QUELLEN.filter((q) => {
            const z = daten[q.id] ?? {};
            const beschaeftigtHier = Boolean(z.waiting) || Boolean(z.running);
            const nochGesperrt = z.locked_until ? restsekunden(z.locked_until) > 0 : false;
            return ! nochGesperrt && ! beschaeftigtHier;
        })
        : [];

    return <>
        <Button size="sm" variant="outline-secondary" onClick={() => setOffen(true)}>
            Datenaktualität
        </Button>

        <Modal show={offen} onHide={() => setOffen(false)} centered size="lg" scrollable>
            <Modal.Header closeButton className="px-4 py-3">
                <Modal.Title className="fs-5 fw-semibold">Datenaktualität</Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-4">
                <p className="text-secondary small mb-4">
                    Wann die angezeigten Daten zuletzt aus der jeweiligen Quelle geholt wurden.
                    Ein Abruf lässt sich hier von Hand anstoßen — danach ist die Quelle zehn
                    Minuten lang für alle gesperrt, damit nicht mehrfach parallel abgerufen wird.
                </p>

                {fehler && <div className="alert alert-danger py-2">{fehler}</div>}

                {daten && <div className="d-flex justify-content-end mb-3">
                    <Button
                        size="sm"
                        variant="primary"
                        disabled={freieQuellen.length === 0 || beschaeftigt || laeuft === "alle"}
                        onClick={() => setFrageAlle(true)}
                    >
                        {laeuft === "alle"
                            ? <><Spinner animation="border" size="sm" className="me-2" />wird gestartet …</>
                            : "Alle aktualisieren"}
                    </Button>
                </div>}

                {laedt && ! daten && <div className="text-center text-muted py-4">
                    <Spinner animation="border" size="sm" className="me-2" />Datenstand wird geladen …
                </div>}

                {daten && QUELLEN.map((quelle) => {
                    const q = daten[quelle.id] ?? {};
                    const rest = restsekunden(q.locked_until);
                    const gesperrt = q.locked_until ? rest > 0 : false;
                    const wartet = Boolean(q.waiting);
                    const laeuftGerade = Boolean(q.running);
                    const arbeitet = wartet || laeuftGerade;
                    const relativ = abstand(q.last_synced_at);
                    // Die Uhr zählt ab dem tatsächlichen Beginn — eine noch
                    // wartende Quelle hat keinen.
                    const seitStart = laeuftGerade ? laufzeit(q.started_at) : 0;
                    // Als auffällig gilt, was deutlich über dem letzten
                    // gemessenen Lauf liegt — bei fehlendem Erfahrungswert
                    // dient eine Minute als grober Anhalt.
                    const erwartet = q.last_duration_seconds || 60;
                    const ueberfaellig = laeuftGerade && seitStart > erwartet * 2;

                    return <div key={quelle.id} className="border rounded-3 p-3 mb-3">
                        <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
                            <div style={{minWidth: 0}}>
                                <div className="fw-semibold">{quelle.name}</div>
                                <div className="text-secondary small">{quelle.beschreibung}</div>
                                <div className="mt-2 small">
                                    Zuletzt abgerufen: <strong>{zeitpunkt(q.last_synced_at)}</strong>
                                    {relativ && <span className="text-secondary"> ({relativ})</span>}
                                </div>
                                {quelle.teile.length > 0 && q.details && <ul className="list-unstyled mt-2 mb-0 small text-secondary">
                                    {quelle.teile.map(([schluessel, bezeichnung]) => (
                                        <li key={schluessel}>{bezeichnung}: {zeitpunkt(q.details[schluessel])}</li>
                                    ))}
                                </ul>}
                                {q.status === "failed" && <div className="text-danger small mt-2">
                                    Der letzte Abruf ist fehlgeschlagen.
                                </div>}
                            </div>

                            <div className="text-end flex-shrink-0">
                                <Button
                                    size="sm"
                                    variant="outline-primary"
                                    disabled={gesperrt || beschaeftigt || laeuft === quelle.id}
                                    onClick={() => aktualisieren(quelle.id)}
                                >
                                    {laeuftGerade || laeuft === quelle.id
                                        ? <><Spinner animation="border" size="sm" className="me-2" />Wird abgerufen …</>
                                        : wartet ? "Wartet …" : "Jetzt abrufen"}
                                </Button>

                                {/* Angefordert, aber noch nicht an der Reihe. */}
                                {wartet && <div className="text-secondary mt-1" style={{fontSize: ".75rem"}}>
                                    wartet auf die vorherige Quelle
                                </div>}

                                {/* Läuft gerade: Uhr ab dem tatsächlichen Beginn,
                                    daneben der Erfahrungswert zum Vergleich. */}
                                {laeuftGerade && <div className="mt-1" style={{fontSize: ".75rem"}}>
                                    <div className={ueberfaellig ? "text-warning-emphasis fw-semibold" : "text-secondary"}>
                                        läuft seit {uhrzeitAusSekunden(seitStart)}
                                    </div>
                                    {q.last_duration_seconds
                                        ? <div className="text-secondary">
                                            zuletzt {dauerText(q.last_duration_seconds)}
                                        </div>
                                        : null}
                                    {ueberfaellig && <div className="text-warning-emphasis">
                                        dauert länger als sonst
                                    </div>}
                                </div>}

                                {/* Nicht am Laufen, aber gesperrt: Countdown bis zur Freigabe. */}
                                {gesperrt && ! arbeitet && <div className="text-secondary mt-1" style={{fontSize: ".75rem"}}>
                                    Abruf wieder möglich in {uhrzeitAusSekunden(rest)}
                                </div>}

                                {/* Ruhezustand: Erwartungswert, damit vor dem Klick
                                    klar ist, worauf man sich einlässt. */}
                                {! arbeitet && ! gesperrt && ! beschaeftigt && q.last_duration_seconds
                                    ? <div className="text-secondary mt-1" style={{fontSize: ".75rem"}}>
                                        dauert etwa {dauerText(q.last_duration_seconds)}
                                    </div>
                                    : null}
                            </div>
                        </div>
                    </div>;
                })}

                <p className="text-secondary mb-0" style={{fontSize: ".8rem"}}>
                    Unabhängig davon holt das System die Daten selbstständig: Lagerbestand,
                    Reservierungen und offene Mengen stündlich, die Shop-Zustände stündlich, die Preise
                    einmal täglich in der Nacht.
                </p>
            </Modal.Body>
        </Modal>

        <Modal show={frageAlle} onHide={() => setFrageAlle(false)} centered>
            <Modal.Header closeButton className="px-4 py-3">
                <Modal.Title className="fs-6 fw-semibold">Alle Quellen abrufen?</Modal.Title>
            </Modal.Header>
            <Modal.Body className="px-4 py-3">
                <p className="mb-2">
                    Es werden {freieQuellen.length === 1 ? "eine Quelle" : `${freieQuellen.length} Quellen`} nacheinander
                    abgerufen: {freieQuellen.map((q) => q.name).join(", ")}.
                </p>
                <p className="text-secondary small mb-0">
                    Das dauert insgesamt etwa {dauerText(
                        freieQuellen.reduce((summe, q) => summe + (daten?.[q.id]?.last_duration_seconds || 60), 0)
                    )}. Danach sind alle abgerufenen Quellen zehn Minuten lang für
                    alle gesperrt.
                </p>
            </Modal.Body>
            <Modal.Footer className="px-4 py-3">
                <Button variant="outline-secondary" size="sm" onClick={() => setFrageAlle(false)}>
                    Abbrechen
                </Button>
                <Button variant="primary" size="sm" onClick={alleAktualisieren}>
                    Ja, alle abrufen
                </Button>
            </Modal.Footer>
        </Modal>
    </>;
}
